import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import { Bell, Volume2, VolumeX } from 'lucide-react'
import { useAuth } from '../../../../app/modules/auth'
import { TOKENS } from '../../../../app/components/design-system'
import {
  fetchAlarmedBoxes,
} from '../../../../app/pages/sensors-v2/core/_requests'
import { subscribeAlarmChanged } from '../../../../app/pages/sensors-v2/transports/WebSocketTransport'

const SOUND_PREF_KEY = 'alarm_bell_sound_enabled'
const SEEN_SET_KEY = 'alarm_bell_seen_box_ids'

// Web-Audio synthesized double-beep. Avoids bundling a binary asset and
// works in every modern browser. Volume is intentionally restrained — this
// fires every time a new box becomes alarmed, so an aggressive sound would
// be hostile during an active incident burst.
function playBeep(): void {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext
    const ctx = new AudioCtx()
    const tone = (freq: number, when: number, dur: number) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.frequency.value = freq
      osc.type = 'sine'
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + when)
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + when + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + when + dur)
      osc.start(ctx.currentTime + when)
      osc.stop(ctx.currentTime + when + dur)
    }
    tone(880, 0, 0.18)
    tone(660, 0.2, 0.25)
  } catch {
    /* ignore — autoplay restrictions or no audio */
  }
}

function loadSeenSet(): Set<number> {
  try {
    const raw = localStorage.getItem(SEEN_SET_KEY)
    if (!raw) return new Set()
    const arr = JSON.parse(raw) as number[]
    return new Set(arr.filter((n) => typeof n === 'number'))
  } catch {
    return new Set()
  }
}

function persistSeenSet(s: Set<number>): void {
  try {
    localStorage.setItem(SEEN_SET_KEY, JSON.stringify(Array.from(s).slice(-200)))
  } catch {
    /* ignore quota */
  }
}

const ALARM_LABEL: Record<string, string> = {
  door: 'Porta',
  bolt: 'Trava',
  door_lock: 'Fechadura',
  vibration: 'Vibração',
  energy: 'Energia',
  sound: 'Som',
  violation: 'Violação',
  request_opening_of_safe: 'Abertura do cofre',
  maximum_time_for_open_door: 'Porta aberta há muito',
  cable_cut_alarm: 'Cabo cortado',
}

export function AlarmNotificationBell() {
  const { currentUser } = useAuth()
  const companyId = currentUser?.last_company_id ? +currentUser.last_company_id : undefined

  const [open, setOpen] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
    try { return localStorage.getItem(SOUND_PREF_KEY) !== 'false' } catch { return true }
  })
  const seenRef = useRef<Set<number>>(loadSeenSet())
  const lastSetRef = useRef<Set<number>>(new Set())
  const rootRef = useRef<HTMLDivElement>(null)

  // Outside-click dismissal so the dropdown feels native.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const { data, refetch } = useQuery(
    ['alarm-bell', companyId],
    () => fetchAlarmedBoxes({
      sort: 'last_activity_desc',
      page: 1,
      items_per_page: 50,
      company_id: companyId,
    }),
    {
      enabled: companyId !== undefined && companyId > 0,
      refetchOnWindowFocus: false,
      // Safety polling — WS push handles the realtime case; this is the
      // fallback for tabs that lose WS without us noticing.
      refetchInterval: 30_000,
      keepPreviousData: true,
    },
  )

  // Re-fetch on every WS alarm_changed event so the bell stays accurate
  // even when no sensors-v2 / map page is mounted to drive the cache.
  useEffect(() => {
    return subscribeAlarmChanged(() => { refetch() })
  }, [refetch])

  const boxes = useMemo(() => data?.data ?? [], [data?.data])

  // Detect new alarms (ids present now but absent from the previous tick)
  // and beep. Runs in an effect so the very first render doesn't trigger
  // a sound for every existing alarm.
  useEffect(() => {
    const currentIdsArr: number[] = boxes.map((b) => Number(b.box_id))
    const currentIds = new Set<number>(currentIdsArr)
    const prev = lastSetRef.current
    let hasNew = false
    for (let i = 0; i < currentIdsArr.length; i += 1) {
      if (!prev.has(currentIdsArr[i])) { hasNew = true; break }
    }
    if (hasNew && prev.size > 0 && soundEnabled) {
      playBeep()
    }
    lastSetRef.current = currentIds
  }, [boxes, soundEnabled])

  const unreadCount = useMemo(() => {
    let n = 0
    for (const b of boxes) if (!seenRef.current.has(Number(b.box_id))) n += 1
    return n
  }, [boxes])

  const totalCount = boxes.length

  const handleOpen = () => {
    setOpen((v) => !v)
    if (!open) {
      // Marking everything currently alarmed as "seen" clears the badge.
      const next = new Set(seenRef.current)
      for (const b of boxes) next.add(Number(b.box_id))
      seenRef.current = next
      persistSeenSet(next)
    }
  }

  const toggleSound = () => {
    setSoundEnabled((v) => {
      const next = !v
      try { localStorage.setItem(SOUND_PREF_KEY, String(next)) } catch { /* ignore */ }
      return next
    })
  }

  const badgeColor = totalCount > 0 ? TOKENS.color.severity.high.text : TOKENS.color.muted

  return (
    <div ref={rootRef} style={{ position: 'relative', marginRight: TOKENS.spacing.md }}>
      <button
        type='button'
        onClick={handleOpen}
        title={totalCount > 0 ? `${totalCount} caixa(s) alarmada(s)` : 'Nenhuma caixa alarmada'}
        style={{
          position: 'relative',
          width: 38,
          height: 38,
          borderRadius: TOKENS.radius.card,
          border: `1px solid ${TOKENS.color.border}`,
          background: TOKENS.color.card,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: badgeColor,
        }}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span
            aria-label={`${unreadCount} alarme(s) sem leitura`}
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: TOKENS.color.severity.high.text,
              boxShadow: '0 0 0 2px #fff',
            }}
          />
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          width: 340,
          maxWidth: '90vw',
          background: TOKENS.color.card,
          border: `1px solid ${TOKENS.color.border}`,
          borderRadius: TOKENS.radius.card,
          boxShadow: '0 12px 28px rgba(17,24,39,0.16)',
          zIndex: 1100,
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 14px',
            borderBottom: `1px solid ${TOKENS.color.border}`,
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: TOKENS.color.text }}>
                Alarmes ativos
              </div>
              <div style={{ fontSize: 11, color: TOKENS.color.muted, marginTop: 2 }}>
                {totalCount === 0 ? 'Tudo limpo agora' : `${totalCount} caixa(s) com alarme`}
              </div>
            </div>
            <button
              type='button'
              onClick={toggleSound}
              title={soundEnabled ? 'Desativar som de alerta' : 'Ativar som de alerta'}
              style={{
                width: 30, height: 30, borderRadius: TOKENS.radius.chip,
                border: `1px solid ${TOKENS.color.border}`,
                background: soundEnabled ? TOKENS.color.actionSoft : TOKENS.color.card,
                color: soundEnabled ? TOKENS.color.action : TOKENS.color.muted,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </button>
          </div>

          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {boxes.length === 0 ? (
              <div style={{
                padding: '24px 16px',
                fontSize: 13,
                color: TOKENS.color.muted,
                textAlign: 'center',
              }}>
                Nenhuma caixa alarmada no momento.
              </div>
            ) : (
              boxes.slice(0, 10).map((box) => {
                const types = (box.active_alarms ?? []).map((a: any) => ALARM_LABEL[a.type] ?? a.type).slice(0, 3).join(', ')
                const more = (box.active_alarms?.length ?? 0) - 3
                return (
                  <Link
                    key={box.box_id}
                    to={`/sensors-v2?box=${box.box_id}`}
                    onClick={() => setOpen(false)}
                    style={{
                      display: 'block',
                      padding: '10px 14px',
                      borderBottom: `1px solid ${TOKENS.color.divider}`,
                      textDecoration: 'none',
                      color: TOKENS.color.text,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>
                        CMB {box.cmb_number ?? '—'}
                      </div>
                      <div style={{ fontSize: 11, color: TOKENS.color.muted }}>
                        {box.company_name ?? ''}
                      </div>
                    </div>
                    <div style={{ marginTop: 4, fontSize: 12, color: TOKENS.color.severity.high.text, fontWeight: 600 }}>
                      {types}{more > 0 ? ` +${more}` : ''}
                    </div>
                  </Link>
                )
              })
            )}
          </div>

          {boxes.length > 0 && (
            <Link
              to='/sensors-v2'
              onClick={() => setOpen(false)}
              style={{
                display: 'block',
                padding: '12px 14px',
                textAlign: 'center',
                fontSize: 12,
                fontWeight: 700,
                color: TOKENS.color.action,
                textDecoration: 'none',
                background: TOKENS.color.surface,
                borderTop: `1px solid ${TOKENS.color.border}`,
              }}
            >
              Ver todas em Sensores →
            </Link>
          )}
        </div>
      )}
    </div>
  )
}
